import { useMemo, useState, useEffect } from 'react';
import { ArrowUp, ArrowDown, ArrowUpDown, Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TableHead } from '@/components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';

export type FilterType =
  | 'text'
  | 'date-range'
  | 'select'
  | 'searchable-select'
  | 'number-range';

export interface ColumnDef<T> {
  key: string;
  label: string;
  defaultVisible?: boolean;
  sortKey?: string;
  filterType?: FilterType;
  filterOptions?: { label: string; value: string }[];
  getValue: (item: T) => string | number | null;
  render?: (item: T) => React.ReactNode;
  align?: 'left' | 'center' | 'right';
  noFilter?: boolean;
  className?: string;
  headerClassName?: string;
}

export function escapeCSVValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  let str = String(value);
  if (/^[=+\-@\t\r]/.test(str)) str = "'" + str;
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

export function exportToCSV<T>(
  items: T[],
  columns: ColumnDef<T>[],
  filename: string,
): void {
  const exportCols = columns.filter((c) => !c.noFilter);
  const header = exportCols.map((c) => escapeCSVValue(c.label)).join(',');
  const rows = items.map((item) =>
    exportCols.map((c) => escapeCSVValue(c.getValue(item))).join(','),
  );
  const csvContent = [header, ...rows].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function copyToClipboard<T>(
  items: T[],
  columns: ColumnDef<T>[],
): Promise<void> {
  const exportCols = columns.filter((c) => !c.noFilter);
  const header = exportCols.map((c) => c.label).join('\t');
  const rows = items.map((item) =>
    exportCols.map((c) => String(c.getValue(item) ?? '')).join('\t'),
  );
  const text = [header, ...rows].join('\n');
  return navigator.clipboard.writeText(text);
}

export function getDefaultVisibility<T>(columns: ColumnDef<T>[]): Record<string, boolean> {
  const defaults: Record<string, boolean> = {};
  for (const col of columns) {
    defaults[col.key] = col.defaultVisible !== false;
  }
  return defaults;
}

export function loadColumnVisibility<T>(
  storageKey: string,
  columns: ColumnDef<T>[],
): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(storageKey);
    if (stored) return { ...getDefaultVisibility(columns), ...JSON.parse(stored) };
  } catch { /* ignore */ }
  return getDefaultVisibility(columns);
}

export function saveColumnVisibility(storageKey: string, visibility: Record<string, boolean>) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(visibility));
  } catch { /* ignore */ }
}

export function loadColumnFilters(storageKey: string): Record<string, string> {
  try {
    const raw = localStorage.getItem(storageKey);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function filterItems<T>(
  items: T[],
  columns: ColumnDef<T>[],
  columnFilters: Record<string, string>,
): T[] {
  const activeFilters = Object.entries(columnFilters).filter(([, v]) => v);
  if (activeFilters.length === 0) return items;

  return items.filter((item) =>
    activeFilters.every(([colKey, filterValue]) => {
      const col = columns.find((c) => c.key === colKey);
      if (!col || col.noFilter) return true;

      const rawValue = col.getValue(item);

      if (col.filterType === 'date-range') {
        const [from, to] = filterValue.split('>>');
        const dateStr = String(rawValue || '');
        if (from && dateStr < from) return false;
        if (to && dateStr > to + 'T23:59:59') return false;
        return true;
      }

      if (col.filterType === 'select' || col.filterType === 'searchable-select') {
        try {
          const selected = JSON.parse(filterValue) as string[];
          return selected.length === 0 || selected.includes(String(rawValue ?? ''));
        } catch { return true; }
      }

      if (col.filterType === 'number-range') {
        const [minStr, maxStr] = filterValue.split('>>');
        const num = Number(rawValue);
        if (Number.isNaN(num)) return false;
        if (minStr !== '' && minStr !== undefined && num < Number(minStr)) return false;
        if (maxStr !== '' && maxStr !== undefined && num > Number(maxStr)) return false;
        return true;
      }

      return String(rawValue ?? '').toLowerCase().includes(filterValue.toLowerCase());
    }),
  );
}

export function sortItems<T>(
  items: T[],
  columns: ColumnDef<T>[],
  sortKey: string | null,
  sortDirection: 1 | -1,
): T[] {
  if (!sortKey) return items;
  const col = columns.find((c) => c.sortKey === sortKey);
  if (!col) return items;
  return [...items].sort((a, b) => {
    const va = col.getValue(a);
    const vb = col.getValue(b);
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    if (typeof va === 'number' && typeof vb === 'number') {
      return (va - vb) * sortDirection;
    }
    return String(va).localeCompare(String(vb)) * sortDirection;
  });
}

export function SortableHeader({
  label,
  sortKey,
  currentSortKey,
  sortDirection,
  onSort,
  align,
}: {
  label: string;
  sortKey: string;
  currentSortKey: string | null;
  sortDirection: 1 | -1;
  onSort: (key: string) => void;
  align?: 'left' | 'center' | 'right';
}) {
  const isActive = currentSortKey === sortKey;
  return (
    <Button
      variant="ghost"
      size="sm"
      className={cn('-ml-3 h-8 hover:bg-transparent', align === 'right' && 'ml-0 -mr-3')}
      onClick={() => onSort(sortKey)}
    >
      {label}
      {isActive ? (
        sortDirection === 1
          ? <ArrowUp className="ml-1 h-3 w-3" />
          : <ArrowDown className="ml-1 h-3 w-3" />
      ) : (
        <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />
      )}
    </Button>
  );
}

function SearchableSelectFilter({
  colKey,
  options,
  value,
  onChange,
}: {
  colKey: string;
  options: { label: string; value: string }[];
  value: string;
  onChange: (key: string, value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selected: string[] = useMemo(() => {
    try { return value ? JSON.parse(value) : []; }
    catch { return []; }
  }, [value]);

  const filtered = useMemo(() => {
    const base = search
      ? options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
      : options;
    if (selected.length === 0) return base;
    const sel = new Set(selected);
    return [...base].sort((a, b) => {
      const aS = sel.has(a.value) ? 0 : 1;
      const bS = sel.has(b.value) ? 0 : 1;
      return aS - bS;
    });
  }, [options, search, selected]);

  const toggle = (opt: string) => {
    const next = selected.includes(opt)
      ? selected.filter((o) => o !== opt)
      : [...selected, opt];
    onChange(colKey, next.length ? JSON.stringify(next) : '');
  };

  const selectedLabel = useMemo(() => {
    if (selected.length === 0) return null;
    if (selected.length === 1) {
      const match = options.find((o) => o.value === selected[0]);
      return match?.label || '1 selected';
    }
    return `${selected.length} selected`;
  }, [selected, options]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="relative">
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-7 w-full text-xs justify-between font-normal">
            <span className="truncate">{selectedLabel || '\u2014'}</span>
            <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        {selected.length > 0 && (
          <button
            type="button"
            onClick={() => { onChange(colKey, ''); setOpen(false); }}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 z-10"
            aria-label="Clear filter"
          >
            <X className="h-3 w-3 opacity-50 hover:opacity-100" />
          </button>
        )}
      </div>
      <PopoverContent className="w-56 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search..." value={search} onValueChange={setSearch} className="h-8 text-xs" />
          <CommandList>
            <CommandEmpty className="py-2 text-center text-xs text-muted-foreground">No results</CommandEmpty>
            <CommandGroup className="max-h-48 overflow-y-auto">
              {filtered.map((opt) => (
                <CommandItem key={opt.value} value={opt.value} onSelect={() => toggle(opt.value)} className="text-xs">
                  <div className={cn('mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                    selected.includes(opt.value) ? 'bg-primary text-primary-foreground' : 'opacity-50')}>
                    {selected.includes(opt.value) && <Check className="h-3 w-3" />}
                  </div>
                  {opt.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function renderColumnFilter<T>(
  col: ColumnDef<T>,
  columnFilters: Record<string, string>,
  setColumnFilter: (key: string, value: string) => void,
  searchableSelectOptions?: Record<string, { label: string; value: string }[]>,
) {
  if (col.noFilter) return <TableHead key={`filter-${col.key}`} className={col.headerClassName} />;

  if (col.filterType === 'searchable-select') {
    const options = searchableSelectOptions?.[col.key] || [];
    return (
      <TableHead key={`filter-${col.key}`} className={cn('p-1', col.headerClassName)}>
        <SearchableSelectFilter
          colKey={col.key}
          options={options}
          value={columnFilters[col.key] || ''}
          onChange={setColumnFilter}
        />
      </TableHead>
    );
  }

  if (col.filterType === 'select' && col.filterOptions) {
    const selected: string[] = (() => {
      try { return columnFilters[col.key] ? JSON.parse(columnFilters[col.key]) : []; }
      catch { return []; }
    })();

    const toggle = (opt: string) => {
      const next = selected.includes(opt)
        ? selected.filter((o) => o !== opt)
        : [...selected, opt];
      setColumnFilter(col.key, next.length ? JSON.stringify(next) : '');
    };

    return (
      <TableHead key={`filter-${col.key}`} className={cn('p-1', col.headerClassName)}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-7 w-full text-xs justify-start font-normal">
              {selected.length ? `${selected.length} selected` : '\u2014'}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-44">
            {col.filterOptions.map((opt) => (
              <DropdownMenuCheckboxItem
                key={opt.value}
                checked={selected.includes(opt.value)}
                onCheckedChange={() => toggle(opt.value)}
              >
                {opt.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableHead>
    );
  }

  if (col.filterType === 'date-range') {
    const raw = columnFilters[col.key] || '';
    const [from = '', to = ''] = raw.split('>>');
    return (
      <TableHead key={`filter-${col.key}`} className={cn('p-1', col.headerClassName)}>
        <div className="flex flex-col gap-0.5">
          <Input
            type="date"
            className="h-6 text-xs px-1"
            value={from}
            onChange={(e) => setColumnFilter(col.key, `${e.target.value}>>${to}`)}
            title="From"
          />
          <Input
            type="date"
            className="h-6 text-xs px-1"
            value={to}
            onChange={(e) => setColumnFilter(col.key, `${from}>>${e.target.value}`)}
            title="To"
          />
        </div>
      </TableHead>
    );
  }

  if (col.filterType === 'number-range') {
    const raw = columnFilters[col.key] || '';
    const [min = '', max = ''] = raw.split('>>');
    return (
      <TableHead key={`filter-${col.key}`} className={cn('p-1', col.headerClassName)}>
        <div className="flex flex-col gap-0.5">
          <Input
            type="number"
            className="h-6 text-xs px-1"
            value={min}
            onChange={(e) => setColumnFilter(col.key, `${e.target.value}>>${max}`)}
            placeholder="Min"
          />
          <Input
            type="number"
            className="h-6 text-xs px-1"
            value={max}
            onChange={(e) => setColumnFilter(col.key, `${min}>>${e.target.value}`)}
            placeholder="Max"
          />
        </div>
      </TableHead>
    );
  }

  return (
    <TableHead key={`filter-${col.key}`} className={cn('p-1', col.headerClassName)}>
      <Input
        className="h-7 text-xs"
        value={columnFilters[col.key] || ''}
        onChange={(e) => setColumnFilter(col.key, e.target.value)}
        placeholder="..."
      />
    </TableHead>
  );
}

export function usePersistedColumnFilters(storageKey: string) {
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(
    () => loadColumnFilters(storageKey),
  );

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(columnFilters));
    } catch { /* ignore */ }
  }, [storageKey, columnFilters]);

  const setColumnFilter = (key: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearColumnFilters = () => setColumnFilters({});

  const hasActiveColumnFilters = Object.values(columnFilters).some(Boolean);

  return { columnFilters, setColumnFilter, clearColumnFilters, hasActiveColumnFilters };
}

export function usePersistedColumnVisibility<T>(storageKey: string, columns: ColumnDef<T>[]) {
  const [columnVisibility, setColumnVisibilityState] = useState<Record<string, boolean>>(
    () => loadColumnVisibility(storageKey, columns),
  );

  const toggleColumn = (key: string) => {
    setColumnVisibilityState((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      saveColumnVisibility(storageKey, next);
      return next;
    });
  };

  const isVisible = (key: string) => columnVisibility[key] !== false;

  return { columnVisibility, toggleColumn, isVisible };
}
